import { BaseControlDefBase, BaseElementDef, BaseElementDefBase } from "./BaseElementDef";
import { BehaviorSubject, combineLatest, from, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { toUpperCaseList } from "../../lib/caseOperations";
import { parseBool } from "../../lib/calculations";
import { VisibleEnum } from "@cs/VisibleEnum";
import { PageSettings } from "@cs-ts/page-settings";
import { FormPageItem } from "@cs/FormPageItem";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { CalculationContext } from "./__CalculationContextInterface";
import type FormDef from "./FormDef";
import type StateFormDef from "./StateFormDef";
import { useMainStore } from "@/mainStore";
import { KnownFeatureFlags } from "@/cs-ts-bridge/cs/KnownFeatureFlags";

export default class PageDef extends BaseElementDef<PageSettings, FormDefinitionItem<PageSettings>, FormPageItem> {
    declare _status$: BehaviorSubject<string | null>;
    declare status: string | null;
    declare isReviewOnly: boolean;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormPageItem, definitionData: FormDefinitionItem<PageSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFPage"; // TODO - not hardcoded?
        this._status$ = this._status$ || new BehaviorSubject(null);
    }
    getData(excludeChildren?: boolean) {
        const result = super.getData(excludeChildren) as FormPageItem;
        if (result != null) {
            result.status = this.status!;
        }
        return result;
    }
    setData(data: FormPageItem, excludeChildren?: boolean) {
        super.setData(data, excludeChildren);
        if (data == null) {
            return;
        }
        this.setStatus(data.status);
    }
    setStatus(status: string) {
        this.status = status;
        if (this._status$ == null) {
            this._status$ = new BehaviorSubject(status as string | null);
        } else {
            this._status$.next(status);
        }
    }

    _validateUneditedChild$(child: BaseElementDefBase) {
        return combineLatest([super._validateChild$(child), this._status$.pipe(map(i => i != "NotStarted"))]).pipe(map(([a, b]) => a && b));
    }

    permitLaterPages$() {
        if (this.definition.settings.mustBeCompletedToProceed) {
            return this.clientStatus$().pipe(/*tap(i => { debugger; }), */ map(i => i == "Complete"));
        } else {
            return from([true]);
        }
    }
    getSegment() : string | undefined  {
        return this.definition.settings.segment;
    }

    visible$() {
        return super.visible$().pipe(switchMap(v => (v ? this.isEnabled$(null) : from([false]))));
    }

    isVisible(mode: string, status: string, segment: string | null, visibleFlag?: boolean) {
        return this._isDisplay(mode, status, segment, visibleFlag) != null;
    }
    isEditable(mode: string, status: string, segment: string | null, visibleFlag?: boolean) {
        return this._isDisplay(mode, status, segment, visibleFlag) == "edit";
    }
    isViewOnly(mode: string, status: string, segment: string | null, visibleFlag?: boolean) {
        return this._isDisplay(mode, status, segment, visibleFlag) == "display";
    }
    _isDisplay(mode: string, status: string, segment: string | null, visibleFlag?: boolean) {
        visibleFlag = visibleFlag ?? useMainStore().isFeatureFlagActive(KnownFeatureFlags.VisibilityModeIsVisibility.Id);
        const pageSegment = this.definition.settings.segment;
        const pageModel =
            pageSegment == null
                ? null
                : (this.getRoot()
                      .getElementsByFilter(i => i.id == pageSegment)
                      .find(i => true) as StateFormDef);
        if (pageModel == null) {
            // non-workflow or non-segment compatibility
            const effectiveStatuses = (this.getRoot() as FormDef).definition.effectiveStatuses;
            const currentStatus = effectiveStatuses?.find(i => i.value == status);
            const editing = currentStatus == null ? mode == "Public" && (status == "Returned" || status == "InProgress") : currentStatus.mode == (mode == "Public" ? "User" : "Admin");
            if (editing) {
                return !visibleFlag || this._isVisibleMode(VisibleEnum.Edit) ? "edit" : null;
            } else {
                return !visibleFlag || this._isVisibleMode(VisibleEnum.Display) ? "display" : null;
            }
        }
        if (pageModel != null) {
            const asReviewer = mode != "Public";
            if (pageModel.can("edit", asReviewer) && (segment == null || pageSegment == segment) && (!visibleFlag || this._isVisibleMode(VisibleEnum.Edit))) {
                return "edit";
            }
            if (pageModel.can("assign", asReviewer) && (segment == null || pageSegment == segment) && (!visibleFlag || this._isVisibleMode(VisibleEnum.Edit))) {
                return "assign";
            }
            if (asReviewer && pageModel.can("view", asReviewer) && (!visibleFlag || this._isVisibleMode(VisibleEnum.Display))) {
                return "display";
            }
        }
        return null;
    }

    _isVisibleMode(mode: VisibleEnum) {
        const visibleModes = this.definition.settings.visible;
        if (visibleModes == null || visibleModes.length == 0) return true;
        return visibleModes.includes(mode);
    }

    isEnabled$(type: string | null): Observable<boolean> {
        const visibleFlag = useMainStore().isFeatureFlagActive(KnownFeatureFlags.VisibilityModeIsVisibility.Id);
        // using isFeatureFlagActiveAsync causes an delay when loading, hence using a non-promise if one is available
        const visibleFlagO = visibleFlag != null ? from([visibleFlag]) : from(useMainStore().isFeatureFlagActiveAsync(KnownFeatureFlags.VisibilityModeIsVisibility.Id));
        return visibleFlagO.pipe(
            switchMap(visibleFlag => {
                if (visibleFlag || (type == "Edit" ? this._isVisibleMode(VisibleEnum.Edit) : type == "Display" ? this._isVisibleMode(VisibleEnum.Display) : true)) {
                    if (this.definition.settings.calculationId) {
                        const result = this.resolveCalculation(this.definition.settings.calculationId);
                        //@ts-ignore
                        return result.pipe(map(value => value.reduce((a, i) => a || parseBool(i) === 1, false)));
                    } else if (this.definition.settings.otherElementId) {
                        const matches = this.definition.settings.valuesToMatch ? this.definition.settings.valuesToMatch.slice() : [];
                        if (this.definition.settings.valueToMatch) {
                            matches.push(this.definition.settings.valueToMatch);
                        }
                        const upperCaseMatches = toUpperCaseList(matches);
                        const otherElementIsDirectDescendant = this.getElementsByFilter(element => element.id == this.definition.settings.otherElementId).length > 0;
                        if (otherElementIsDirectDescendant) {
                            return from([true]);
                        } else {
                            return this.calculationContext.rootElement!.getElementsById$(this.definition.settings.otherElementId, this.vmId).pipe(
                                switchMap(children => combineLatest(children.map(i => (i as BaseControlDefBase).effectiveValueList$()))),
                                map(valueLists => Array.prototype.concat.apply([], valueLists)),
                                map(valueList => toUpperCaseList(valueList)),
                                map(upperValueList => (this.definition.settings.invert || false) != upperValueList.some(v => upperCaseMatches.some(m => v == m))),
                            );
                        }
                    } else {
                        return from([true]);
                    }
                } else {
                    return from([false]);
                }
            }),
        );
    }

    clientStatus$() {
        return this._status$.pipe(switchMap(status => (status == null ? this.valid$().pipe(map(valid => (valid ? "Complete" : "Invalid"))) : from([status]))));
    }
}
